import { Icon, useFormatMessageGeneric } from '@main/core-ui';
import React, { createContext, useContext } from 'react';

import {
  StepProps,
  StepsContextType,
  StepSize,
  StepsProps,
  StepStatus,
} from './types';
import {
  Divider,
  StepContainer,
  StepContent,
  StepDescription,
  StepIndicator,
  StepsContainer,
  StepTitle,
} from './wrappers';

/**
 * Context for sharing step state between Steps and Step components
 */
const StepsContext = createContext<StepsContextType>({
  currentIndex: 0,
  globalStatus: StepStatus.Process,
  size: StepSize.Default,
});

/**
 * Extended StepProps that includes the index property
 */
interface ExtendedStepProps extends StepProps {
  /**
   * Index of the step in the sequence
   */
  index?: number;
}

/**
 * Step Component - Represents a single step in the steps flow
 */
const Step: React.FC<ExtendedStepProps> = ({
  title,
  description,
  icon,
  status: explicitStatus,
  style,
  index,
}) => {
  const { formatMessageGeneric } = useFormatMessageGeneric();
  const { currentIndex, globalStatus, size } = useContext(StepsContext);

  /**
   * Determine status based on context and explicit status
   * - Uses explicit status if provided
   * - Otherwise determines status based on step position
   */
  const status =
    explicitStatus ??
    (index === undefined
      ? StepStatus.Wait
      : index < currentIndex
        ? StepStatus.Finish
        : index === currentIndex
          ? globalStatus
          : StepStatus.Process);

  return (
    <StepContainer style={style}>
      <StepIndicator
        $status={status}
        $size={size}
        $isActive={index === currentIndex}
      >
        {icon ??
          (status === StepStatus.Finish ? (
            <Icon type="checkmark-circle" />
          ) : status === StepStatus.Error ? (
            <Icon type="exclamation-circle" />
          ) : (
            <span>{(index ?? 0) + 1}</span>
          ))}
      </StepIndicator>

      {(title || description) && (
        <StepContent>
          {title && <StepTitle>{formatMessageGeneric(title)}</StepTitle>}
          {description && (
            <StepDescription>
              {formatMessageGeneric(description)}
            </StepDescription>
          )}
        </StepContent>
      )}
    </StepContainer>
  );
};

/**
 * Steps Component - Displays a sequence of steps with their status
 */
export const Steps: React.FC<StepsProps> & {
  /**
   * Step subcomponent
   */
  Step: React.FC<ExtendedStepProps>;
} = ({
  current = 0,
  isHorizontal = true,
  status = StepStatus.Process,
  size = StepSize.Default,
  children,
}) => {
  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const contextValue = React.useMemo(
    () => ({
      currentIndex: current,
      globalStatus: status,
      size,
    }),
    [current, status, size],
  );

  // Add indices to children
  const childrenWithIndices = React.Children.map(children, (child, index) => {
    if (React.isValidElement<ExtendedStepProps>(child)) {
      return React.cloneElement(child, { ...child.props, index });
    }
    return child;
  });

  return (
    <StepsContext.Provider value={contextValue}>
      <StepsContainer
        $isHorizontal={isHorizontal}
        role="group"
        aria-label="Steps"
      >
        {React.Children.map(childrenWithIndices, (child, index) => {
          const isLast = index === React.Children.count(children) - 1;
          return (
            <React.Fragment key={index}>
              {child}
              {!isLast && (
                <Divider $isHorizontal={isHorizontal} aria-hidden="true" />
              )}
            </React.Fragment>
          );
        })}
      </StepsContainer>
    </StepsContext.Provider>
  );
};

// Re-export types
export type { StepProps, StepsProps, StepStatus };

// Attach Step component to Steps
Steps.Step = Step;
